import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import { LockAuditLog, LockFormValues, Lock } from '../../security/types';
import { Client } from '../types';

export const clientsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getClients: build.query<Response<Client>, string>({
      query: (query) => ({
        url: `/v1/users${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClient: build.query<{ data: Client }, string>({
      query: (id) => ({
        url: `/v1/users/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateClient: build.mutation<
      { data: Client },
      {
        body: {
          status: 'ACTIVE' | 'DISABLED';
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/users/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetClientPassword: build.mutation<{ data: { token: string } }, string>({
      query: (id) => ({
        url: `/v1/recover`,
        method: 'POST',
        body: {
          userId: id,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientActiveLocks: build.query<Response<Lock>, string>({
      query: (id) => ({
        url: `/v1/locks?take=1&locked=true&userId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createClientLock: build.mutation<{ data: Lock }, LockFormValues>({
      query: (body) => ({
        url: '/v1/locks',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateClientLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getClientLockItemAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetClientsQuery,
  useLazyGetClientQuery,
  useResetClientPasswordMutation,
  useLazyGetClientAuditLogsQuery,
  useLazyGetClientLockItemQuery,
  useLazyGetClientLocksQuery,
  useCreateClientLockMutation,
  useUpdateClientLockMutation,
  useLazyGetClientLockItemAuditLogsQuery,
  useLazyGetClientActiveLocksQuery,
  useUpdateClientMutation,
} = clientsApi;

export const {
  endpoints: {
    getClients,
    getClient,
    getClientAuditLogs,
    getClientLockItem,
    getClientLocks,
    createClientLock,
    updateClientLock,
    getClientLockItemAuditLogs,
    getClientActiveLocks,
    updateClient,
  },
} = clientsApi;
